import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Car series",
    description:
      "The 12GO Car Series 95022 is a detailed brick-built set combining a modern high-performance sports car with a spacious motorhome designed for life on the road. With 1,294+ pieces, the set offers an engaging building experience and an impressive display combination for automotive and travel enthusiasts.\n\nThe sports car features an aggressive modern design with a low profile, detailed front end, sporty bodywork, and a carefully recreated interior. The accompanying motorhome provides a completely different building experience, with a highly detailed exterior and numerous interactive elements.\n\nOne of the highlights is the motorhome's range of opening sections, including doors and windows that allow access to the interior. The large side awning can be extended to create a realistic outdoor living area, while additional exterior equipment helps recreate the atmosphere of a fully equipped road-trip vehicle.\n\nThe two models work perfectly together as a modern travel scene, combining performance and comfort in a single collection. Whether displayed separately or as a complete road-trip setup, the set offers plenty of visual detail and character.\n\nWith its combination of vehicles, interactive features, and 1,294+ pieces, the Car Series 95022 is an excellent choice for collectors, automotive enthusiasts, and builders aged 14 and above.",
    highlightsTitle: "Product Features",
    features: [
      "Detailed sports car",
      "Detailed motorhome",
      "Multiple opening doors and windows",
      "Extendable side awning",
      "Detailed vehicle interiors",
      "Extensive exterior equipment",
      "Designed for display and interactive building",
      "Ideal for automotive and travel enthusiasts",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 95022</li>",
      "<li><strong>Series:</strong> Car Series</li>",
      "<li><strong>Pieces:</strong> 1,294+</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
    ],
  },
  pl: {
    title: "Car series",
    description:
      "12GO Car Series 95022 to wyjątkowy zestaw łączący nowoczesny samochód sportowy z dużym, szczegółowo odwzorowanym kamperem. Zestaw składa się z ponad 1294 elementów i oferuje interesujące doświadczenie budowania oraz efektowną scenę dla miłośników motoryzacji i podróżowania.\n\nSamochód sportowy wyróżnia się dynamiczną sylwetką, niską linią nadwozia, szczegółowo odwzorowanym przednim pasem oraz sportowymi proporcjami. Towarzyszący mu kamper oferuje zupełnie inny rodzaj konstrukcji, z dużą liczbą detali oraz wieloma elementami funkcyjnymi.\n\nJedną z największych atrakcji zestawu są otwierane elementy kampera, w tym drzwi i okna pozwalające zajrzeć do wnętrza. Duża boczna markiza może zostać rozłożona, tworząc realistyczną strefę wypoczynkową. Dodatkowe wyposażenie zewnętrzne pozwala jeszcze lepiej odwzorować charakter w pełni wyposażonego pojazdu turystycznego.\n\nOba modele świetnie się uzupełniają, tworząc nowoczesną scenę podróżniczą łączącą sportowy charakter samochodu z komfortem kampera. Można je prezentować razem lub osobno, tworząc efektowną ekspozycję.\n\nDzięki połączeniu dwóch różnych pojazdów, funkcjonalnych elementów i ponad 1294 części, Car Series 95022 będzie świetnym wyborem dla kolekcjonerów, miłośników motoryzacji oraz fanów modeli z klocków w wieku od 14 lat.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Szczegółowy samochód sportowy",
      "Szczegółowo odwzorowany kamper",
      "Otwierane drzwi i okna",
      "Rozkładana boczna markiza",
      "Szczegółowe wnętrza pojazdów",
      "Bogate wyposażenie zewnętrzne",
      "Model do budowy i ekspozycji",
      "Idealny dla miłośników motoryzacji i podróży",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 95022</li>",
      "<li><strong>Seria:</strong> Car Series</li>",
      "<li><strong>Liczba elementów:</strong> 1294+</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
    ],
  },
  de: {
    title: "Car series",
    description:
      "Die 12GO Car Series 95022 kombiniert einen modernen Sportwagen mit einem detailreichen Reisemobil und schafft damit eine einzigartige Fahrzeugkollektion. Mit mehr als 1.294 Teilen bietet das Set ein abwechslungsreiches Bauerlebnis und eine beeindruckende Präsentation für Auto- und Reiseliebhaber.\n\nDer Sportwagen überzeugt mit einer dynamischen Karosserie, flacher Silhouette, detaillierter Front und sportlichen Proportionen. Das Reisemobil bietet einen völlig anderen Aufbau mit zahlreichen Details und funktionalen Elementen.\n\nZu den Highlights gehören die öffnenden Türen und Fenster des Reisemobils. Die große seitliche Markise lässt sich ausfahren und schafft einen realistischen Außenbereich. Zahlreiche zusätzliche Ausstattungsdetails runden das Erscheinungsbild eines modernen Reise- und Freizeitfahrzeugs ab.\n\nBeide Fahrzeuge ergänzen sich perfekt zu einer modernen Roadtrip-Szene. Sie können gemeinsam oder separat präsentiert werden und bieten dadurch vielseitige Möglichkeiten für die Ausstellung.\n\nMit 1.294+ Teilen, zwei unterschiedlichen Fahrzeugen und zahlreichen Funktionen ist die Car Series 95022 eine ausgezeichnete Wahl für Sammler und Motorsport- sowie Reisebegeisterte ab 14 Jahren.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Detaillierter Sportwagen",
      "Detailreiches Reisemobil",
      "Öffnende Türen und Fenster",
      "Ausziehbare Seitenmarkise",
      "Detaillierte Innenräume",
      "Umfangreiche Außenausrüstung",
      "Für Bau und Ausstellung geeignet",
      "Ideal für Auto- und Reiseliebhaber",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95022</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Teile:</strong> 1.294+</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
    ],
  },
  fr: {
    title: "Car series",
    description:
      "Le 12GO Car Series 95022 associe une voiture de sport moderne à un camping-car richement détaillé pour créer un ensemble original dédié aux passionnés d'automobile et de voyage. Composé de plus de 1 294 pièces, il offre une expérience de construction variée et une superbe présentation.\n\nLa voiture de sport présente une silhouette dynamique, une carrosserie basse et sportive ainsi qu'une face avant soigneusement détaillée. Le camping-car propose une construction totalement différente, avec de nombreux détails extérieurs et plusieurs éléments fonctionnels.\n\nParmi les principaux atouts figurent les portes et fenêtres ouvrantes du camping-car. Le grand auvent latéral peut être déployé afin de créer un espace extérieur réaliste, tandis que les nombreux accessoires complètent l'apparence d'un véhicule de voyage moderne.\n\nLes deux véhicules s'associent parfaitement pour créer une scène de voyage contemporaine. Ils peuvent être exposés ensemble ou séparément selon les préférences de chaque collectionneur.\n\nAvec ses 1 294+ pièces, ses deux véhicules et ses nombreux éléments fonctionnels, le Car Series 95022 constitue un excellent choix pour les collectionneurs et les passionnés d'automobile et de voyage âgés de 14 ans et plus.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Voiture de sport détaillée",
      "Camping-car détaillé",
      "Portes et fenêtres ouvrantes",
      "Auvent latéral déployable",
      "Intérieurs détaillés",
      "Nombreux équipements extérieurs",
      "Modèle destiné à la construction et à l'exposition",
      "Idéal pour les passionnés d'automobile et de voyage",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 95022</li>",
      "<li><strong>Série :</strong> Car Series</li>",
      "<li><strong>Nombre de pièces :</strong> 1 294+</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
    ],
  },
  es: {
    title: "Car series",
    description:
      "El 12GO Car Series 95022 combina un moderno coche deportivo con una autocaravana detallada para crear una completa escena de viaje y automovilismo. Con más de 1.294 piezas, el set ofrece una experiencia de construcción variada y un excelente modelo para exposición.\n\nEl coche deportivo presenta una silueta dinámica, una carrocería baja y numerosos detalles en la parte frontal y el interior. La autocaravana ofrece una construcción mucho más amplia, con una gran cantidad de elementos funcionales y detalles exteriores.\n\nUno de sus principales atractivos son las puertas y ventanas que pueden abrirse, permitiendo acceder visualmente al interior. El toldo lateral puede extenderse para crear una auténtica zona exterior, mientras que los diferentes elementos de equipamiento completan la apariencia de un vehículo de viaje moderno.\n\nLos dos vehículos se complementan perfectamente y permiten crear una atractiva escena de carretera. Pueden mostrarse juntos o por separado, ofreciendo distintas posibilidades de exposición.\n\nCon 1.294+ piezas, dos vehículos diferentes y numerosos elementos funcionales, el Car Series 95022 es una excelente elección para coleccionistas y aficionados al automóvil y los viajes.",
    highlightsTitle: "Características",
    features: [
      "Coche deportivo detallado",
      "Autocaravana detallada",
      "Puertas y ventanas practicables",
      "Toldo lateral extensible",
      "Interiores detallados",
      "Equipamiento exterior completo",
      "Diseñado para construcción y exposición",
      "Ideal para aficionados al automóvil y los viajes",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 95022</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Piezas:</strong> 1.294+</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
    ],
  },
  nl: {
    title: "Car series",
    description:
      "De 12GO Car Series 95022 combineert een moderne sportwagen met een gedetailleerde camper en creëert daarmee een opvallende set voor liefhebbers van auto's en reizen. Met meer dan 1.294 onderdelen biedt de set een afwisselende bouwervaring en een indrukwekkend displaymodel.\n\nDe sportwagen heeft een dynamische vormgeving, een laag sportief profiel, een gedetailleerde voorkant en een zorgvuldig uitgewerkt interieur. De camper biedt een geheel andere bouwervaring dankzij het grotere formaat, vele details en diverse functionele elementen.\n\nEen van de belangrijkste kenmerken zijn de openende deuren en ramen van de camper. De grote zijluifel kan worden uitgeklapt om een realistische buitenruimte te creëren. Extra uitrusting aan de buitenzijde maakt de camper compleet en geeft het model een authentieke reisuitstraling.\n\nDe twee voertuigen vormen samen een moderne roadtrip-scène, maar kunnen ook afzonderlijk worden tentoongesteld. Hierdoor is de set geschikt voor verschillende soorten collecties.\n\nMet 1.294+ onderdelen, twee verschillende voertuigen en meerdere functionele elementen is de Car Series 95022 een uitstekende keuze voor verzamelaars en liefhebbers van auto's en reizen.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Gedetailleerde sportwagen",
      "Gedetailleerde camper",
      "Openende deuren en ramen",
      "Uitklapbare zijluifel",
      "Gedetailleerde interieurs",
      "Uitgebreide externe uitrusting",
      "Geschikt voor bouwen en tentoonstellen",
      "Ideaal voor auto- en reisliefhebbers",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95022</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Onderdelen:</strong> 1.294+</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
    ],
  },
  zh: {
    title: "Car series",
    description:
      "12GO Car Series 95022 将一辆现代运动型汽车与一辆高细节房车组合在一起，为汽车和旅行爱好者带来充满特色的积木套装。整套包含 1,294+ 块积木，兼具丰富的拼搭体验和出色的展示效果。\n\n运动型汽车拥有充满动感的车身设计、低矮的运动化比例、精细的前脸以及细致的内部结构。配套房车则采用更大型的结构设计，拥有丰富的外部细节以及多种功能部件。\n\n房车的一大亮点是多处可开启结构，包括车门和车窗，可以进一步展示内部空间。侧面的遮阳篷可以展开，打造真实的户外休息区域，车身外部的各种装备进一步增强了旅行车辆的真实感。\n\n两款车型组合在一起，可以打造完整的现代公路旅行场景，也可以分别展示，为收藏者提供更多陈列方式。\n\n凭借 1,294+ 块积木、两款不同类型的车辆以及丰富的功能结构，Car Series 95022 非常适合汽车模型收藏家、积木爱好者以及喜欢旅行主题的玩家。",
    highlightsTitle: "产品特点",
    features: [
      "精细运动型汽车",
      "高细节房车",
      "多处车门和车窗可开启",
      "侧面遮阳篷可展开",
      "精细内部结构",
      "丰富外部装备",
      "适合拼搭与展示",
      "适合汽车和旅行主题收藏",
    ],
    specsTitle: "产品参数",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>产品编号：</strong>95022</li>",
      "<li><strong>系列：</strong>Car Series</li>",
      "<li><strong>积木数量：</strong>1,294+ 块</li>",
      "<li><strong>建议年龄：</strong>14 岁以上</li>",
    ],
  },
  it: {
    title: "Car series",
    description:
      "Il 12GO Car Series 95022 combina una moderna auto sportiva con un camper ricco di dettagli, creando un set originale dedicato agli appassionati di automobili e viaggi. Con oltre 1.294 pezzi, offre un'esperienza di costruzione varia e un'ottima presenza da esposizione.\n\nL'auto sportiva presenta una silhouette dinamica, una carrozzeria ribassata, un frontale dettagliato e un abitacolo accuratamente riprodotto. Il camper propone invece una costruzione più ampia, con numerosi dettagli esterni e diverse funzioni interattive.\n\nUno dei punti di forza sono le porte e i finestrini apribili del camper. Il grande tendalino laterale può essere esteso per creare una realistica area esterna, mentre i numerosi accessori completano l'aspetto di un moderno veicolo da viaggio.\n\nI due veicoli si abbinano perfettamente per creare una moderna scena da viaggio, ma possono anche essere esposti separatamente.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Auto sportiva dettagliata",
      "Camper dettagliato",
      "Porte e finestrini apribili",
      "Tendalino laterale estensibile",
      "Interni dettagliati",
      "Ricca dotazione esterna",
      "Ideale per costruzione ed esposizione",
      "Perfetto per appassionati di auto e viaggi",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 95022</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Pezzi:</strong> 1.294+</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
    ],
  },
  ru: {
    title: "Car series",
    description:
      "12GO Car Series 95022 объединяет современный спортивный автомобиль и детализированный кемпер, создавая интересный набор для поклонников автомобилей и путешествий. В комплект входит более 1 294 деталей, благодаря чему модель предлагает увлекательный процесс сборки и эффектный результат.\n\nСпортивный автомобиль отличается динамичным дизайном, низким силуэтом, проработанной передней частью и детализированным салоном. Кемпер имеет более крупную конструкцию и большое количество внешних деталей и функциональных элементов.\n\nОдной из главных особенностей являются открывающиеся двери и окна кемпера. Боковой навес можно развернуть, создавая реалистичную зону отдыха на открытом воздухе. Дополнительное внешнее оборудование усиливает впечатление от модели как от современного туристического автомобиля.\n\nДва автомобиля отлично дополняют друг друга, создавая современную сцену для путешествий. При этом их можно демонстрировать вместе или по отдельности.",
    highlightsTitle: "Характеристики",
    features: [
      "Детализированный спортивный автомобиль",
      "Детализированный кемпер",
      "Открывающиеся двери и окна",
      "Раскладной боковой навес",
      "Детализированный интерьер",
      "Богатое внешнее оснащение",
      "Подходит для сборки и экспонирования",
      "Отличный выбор для автолюбителей и поклонников путешествий",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 95022</li>",
      "<li><strong>Серия:</strong> Car Series</li>",
      "<li><strong>Количество деталей:</strong> 1 294+</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
    ],
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_95022: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
